import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Modal } from 'components/ui/Modal'
import { Alert, AlertDescription } from 'components/ui/Alert'
import { CustomSelect } from 'components/ui/CustomSelect'
import { Loader2 } from 'lucide-react'
import { hiringProcessApi, type HiringProcessTemplate, type Offer } from 'lib/api'

interface CloneOfferModalProps {
  isOpen: boolean
  offer: Offer | null
  onClose: () => void
  onConfirm: (offer: Offer, hiringTemplateId: number | null) => Promise<void>
  isCloning?: boolean
}

export function CloneOfferModal({
  isOpen,
  offer,
  onClose,
  onConfirm,
  isCloning = false,
}: CloneOfferModalProps) {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<HiringProcessTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [loadingTemplates, setLoadingTemplates] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const loadTemplates = async () => {
      setLoadingTemplates(true)
      try {
        const res = await hiringProcessApi.getTemplates()
        const items = Array.isArray(res.data) ? res.data : (res.data as { results?: HiringProcessTemplate[] }).results || []
        const active = items.filter((tpl: HiringProcessTemplate) => tpl.is_active)
        setTemplates(active)

        const sourceTemplateId = offer?.hiring_template ?? null
        if (sourceTemplateId != null && active.some((tpl) => tpl.id === sourceTemplateId)) {
          setSelectedTemplateId(sourceTemplateId)
        } else {
          const defaultTemplate = active.find((tpl) => tpl.is_default)
          setSelectedTemplateId(defaultTemplate ? defaultTemplate.id : active[0]?.id ?? null)
        }
      } catch (error) {
        console.error('Error loading hiring templates:', error)
        setTemplates([])
        setSelectedTemplateId(null)
      } finally {
        setLoadingTemplates(false)
      }
    }

    loadTemplates()
  }, [isOpen, offer?.hiring_template])

  const handleConfirm = async () => {
    if (!offer || selectedTemplateId == null) return
    await onConfirm(offer, selectedTemplateId)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('offers.clone.title', 'Clone Job Offer')}
      className="max-w-lg"
    >
      <div className="space-y-6">
        {offer && (
          <p className="text-sm text-muted-foreground">
            {t('offers.clone.message', {
              title: offer.job_title,
              defaultValue:
                'You are about to duplicate "{{title}}". Choose a hiring process template for the new offer.',
            })}
          </p>
        )}

        <div className="rounded-2xl border bg-card shadow-sm">
          <div className="px-5 py-4 border-b">
            <div className="text-sm font-semibold text-foreground">
              {t('hiringProcess.template', 'Hiring template')}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t(
                'offers.clone.hiringTemplateHelp',
                'Choose which hiring workflow the cloned offer will follow.'
              )}
            </div>
          </div>
          <div className="p-5">
            {loadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading', 'Loading...')}
              </div>
            ) : (
              <>
                <CustomSelect
                  options={templates.map((tpl) => ({
                    value: String(tpl.id),
                    label: tpl.is_default
                      ? `${tpl.name} (${t('common.default', 'Default')})`
                      : tpl.name,
                  }))}
                  value={selectedTemplateId != null ? String(selectedTemplateId) : ''}
                  onChange={(v) => setSelectedTemplateId(v ? Number(v) : null)}
                  placeholder={t(
                    'offers.clone.hiringTemplatePlaceholder',
                    'Select a hiring template'
                  )}
                  disabled={templates.length === 0 || isCloning}
                />
                {templates.length === 0 && (
                  <div className="mt-3">
                    <Alert variant="warning">
                      <AlertDescription>
                        {t(
                          'notifications.noHiringProcessConfigured',
                          'No hiring process configured'
                        )}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isCloning}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isCloning || loadingTemplates || selectedTemplateId == null}
          >
            {isCloning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('offers.clone.cloning', 'Cloning...')}
              </>
            ) : (
              t('offers.clone.confirm', 'Clone Offer')
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
